// ═══════════════════════════════════════════════════════════════
// API CLIENT — Alrifai Backend  (35 endpoints)
// Set NEXT_PUBLIC_API_URL in .env.local
// ═══════════════════════════════════════════════════════════════

const BASE = (process.env.NEXT_PUBLIC_API_URL || 'http://127.0.0.1:8000/api/').replace(/\/$/, '') + '/'

async function get<T>(endpoint: string): Promise<T | null> {
  try {
    const res = await fetch(`${BASE}${endpoint}`, {
      headers: { Accept: 'application/json' },
      next: { revalidate: 60 },
    })
    if (!res.ok) return null
    const j = await res.json()
    return j.key === 'success' ? (j.data as T) : null
  } catch { return null }
}

async function post<T>(endpoint: string, body: object): Promise<T | null> {
  try {
    const res = await fetch(`${BASE}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
    const j = await res.json()
    return j.key === 'success' ? ((j.data ?? j) as T) : null
  } catch { return null }
}

// ─── Types ───────────────────────────────────────────────────

export type Slider        = { id: number; image: string; title: string; description: string }
export type BlogPost      = { id: number; image: string; slug: string; title: string; description: string; created_at: string; likes_count: number; comments_count: number; status?: number; tags?: Tag[]; categories?: Category[]; content?: string }
export type Category      = { id: number; name: string; slug: string; image?: string; icon?: string; meta_title?: string; meta_description?: string; meta_keywords?: string }
export type Tag           = { id: number; name: string; slug: string; count?: number }
export type Comment       = { id: number; name: string; email: string; website?: string; comment: string; created_at: string }
export type Inspiration   = { id: number; image: string; slug: string; title: string; description: string; created_at: string; likes_count: number; comments_count: number; tags?: Tag[]; category?: Category; content?: string }
export type InspirationCat= { id: number; name: string; icon: string; image?: string; slug?: string; count?: number }
export type Business      = { id: number; icon: string; title: string; description: string; image?: string; logo?: string; slug?: string }
export type CoreValue     = { id: number; image: string; title: string; description: string; quotes: string }
export type MissionCard   = { id: number; title: string; contents: { id: number; icon: string; content: string }[] }
export type VisionItem    = { id: number; icon: string; title: string }
export type Social        = { id: number; icon: string; link: string }
export type Insight       = { id: number; title: string; video_url: string; published_at: string }
export type Team          = { id: number; image: string; full_name: string; job_title: string; description: string }
export type Project       = { id: number; image: string; title: string; slug: string; created_by: string; description: string; client_name: string; pdf?: string; doc?: string; meta_title?: string; meta_description?: string }
export type Setting       = { key: string; value: string }
export type Pagination    = { total_items: number; count_items: number; per_page: number; total_pages: number; current_page: number; next_page_url: string; perv_page_url: string }

// ─── 01 · Home ───────────────────────────────────────────────

export const getHomeData = () => get<{
  sliders: Slider[]
  about?: { title: string; description: string; image: string }
  latest_blogs?: BlogPost[]
  core_values?: CoreValue[]
  mission?: MissionCard[]
  vision?: { title: string; description: string; items?: VisionItem[] }
  businesses?: Business[]
}>('home')

// ─── 02-04 · About ───────────────────────────────────────────

export const getBiography = () => get<{
  about_sliders: { id: number; icon: string; title: string; description: string }[]
  career?: { id: number; title: string; description: string }[]
  socials?: Social[]
  stats?: { years_experience: number; people_worked_with: number; projects: number }
  video_url?: string
}>('about/biography')

export const getVision = () => get<{
  core_values: VisionItem[]
  vision?: { title: string; description: string }
  goals?: { id: number; title: string; description: string }[]
}>('about/vision')

export const getMission = () => get<{
  core_values: MissionCard[]
  mission?: { title: string; description: string }
}>('about/mission')

export const getCoreValues = () => get<{
  our_core_values: CoreValue[]
}>('about/our-core-values')

// ─── 05 · Settings ───────────────────────────────────────────

export const getSettings = (keys: string[]) =>
  get<{ settings: Setting[] }>(`settings?${keys.map(k => `keys[]=${k}`).join('&')}`)

export const getSiteSettings = () => getSettings(['site_logo', 'site_fav_icon', 'site_name', 'site_description'])

// ─── 06 · Socials ────────────────────────────────────────────

export const getSocials = () => get<Social[]>('socials')

// ─── 07 · Teams ──────────────────────────────────────────────

export const getTeamDetails = (id: number) => get<Team>(`teams/${id}`)

// ─── 08-09 · Businesses ──────────────────────────────────────

export const getBusinessesPage = () => get<{
  businesses: Business[]
  coming_soon?: Business[]
  current_projects?: Project[]
  worlddef?: { title: string; countdown_date?: string; image?: string }
}>('businesses-page')

export const getBusinessDetails = (id: number) => get<{ business: Business & { projects?: Project[] } }>(`businesses/${id}`)

// ─── 10 · Project details ────────────────────────────────────

export const getProjectDetails = (slug: string) => get<Project>(`projects/${slug}`)

// ─── 11-17 · Blogs ───────────────────────────────────────────

export const getBlogsPage = () => get<{
  latest_blogs: BlogPost[]
  featured_blog?: BlogPost
}>('blogs-page')

export const getBlogs = (p?: { created_at?: string; category_id?: number; status?: string; page?: number; paginate?: number }) => {
  const q = new URLSearchParams()
  if (p?.created_at)   q.set('created_at', p.created_at)
  if (p?.category_id)  q.set('category_id', String(p.category_id))
  if (p?.status)       q.set('status', p.status)
  q.set('page',     String(p?.page    || 1))
  q.set('paginate', String(p?.paginate || 10))
  return get<{ blogs: BlogPost[]; pagination: Pagination }>(`blogs?${q}`)
}

export const getBlogPost   = (slug: string) => get<{ blog: BlogPost }>(`blogs/${slug}`)

export const getBlogCategories = () => get<Category[]>('blog-categories')

export const getCategoryBlogs = (id: number, page = 1, paginate = 20) =>
  get<{ category: Category; blogs: BlogPost[]; pagination: Pagination }>(`blog-categories/${id}?page=${page}&paginate=${paginate}`)

export const getLatestBlogs = (tag?: string) =>
  get<BlogPost[]>(`latest-blogs${tag ? `?popular_tag_slug=${tag}` : ''}`)

export const getPopularTagBlogs = (slug: string) =>
  get<{ blogs: BlogPost[] }>(`popular-tag-blogs/${slug}`)

export const getTagBlogs = (tag: string, page = 1, paginate = 10) =>
  get<{ blogs: BlogPost[]; pagination: Pagination }>(`tag-blogs?tag=${encodeURIComponent(tag)}&page=${page}&paginate=${paginate}`)

export const getBlogComments = (id: number, page = 1) =>
  get<{ comments: Comment[]; pagination: Pagination }>(`blogs/${id}/comments?page=${page}&paginate=20`)

export const addBlogComment = (id: number, body: { name: string; email: string; website?: string; comment: string }) =>
  post<{ comment: Comment }>(`blogs/${id}/comments`, body)

export const toggleBlogLike = (id: number) =>
  post<{ likes_count: number; liked: boolean }>(`blogs/${id}/like`, {})

// ─── 18-24 · Inspirations ────────────────────────────────────

export const getInspirations = (p?: { category_id?: number; page?: number; paginate?: number }) => {
  const q = new URLSearchParams()
  if (p?.category_id) q.set('category_id', String(p.category_id))
  q.set('page',     String(p?.page    || 1))
  q.set('paginate', String(p?.paginate || 10))
  return get<{ inspirations: Inspiration[]; pagination: Pagination }>(`inspirations?${q}`)
}

export const getInspirationPost = (slug: string) =>
  get<{ inspiration: Inspiration }>(`inspirations/${slug}`)

export const getInspirationCategories = () => get<InspirationCat[]>('inspiration-categories')

export const getCategoryInspirations = (id: number, page = 1, paginate = 20) =>
  get<{ category: InspirationCat; inspirations: Inspiration[]; pagination: Pagination }>(`inspiration-categories/${id}?page=${page}&paginate=${paginate}`)

export const getLatestInspirations = (tag?: string) =>
  get<Inspiration[]>(`latest-inspirations${tag ? `?popular_tag_slug=${tag}` : ''}`)

export const getTagInspirations = (tag: string, page = 1, paginate = 10) =>
  get<{ inspirations: Inspiration[]; pagination: Pagination }>(`tag-inspirations?tag=${encodeURIComponent(tag)}&page=${page}&paginate=${paginate}`)

export const getInspirationComments = (id: number, page = 1) =>
  get<{ comments: Comment[]; pagination: Pagination }>(`inspirations/${id}/comments?page=${page}&paginate=20`)

export const addInspirationComment = (id: number, body: { name: string; email: string; website?: string; comment: string }) =>
  post<{ comment: Comment }>(`inspirations/${id}/comments`, body)

export const toggleInspirationLike = (id: number) =>
  post<{ likes_count: number; liked: boolean }>(`inspirations/${id}/like`, {})

// ─── 25 · Insights ───────────────────────────────────────────

export const getInsights = (page = 1, paginate = 15) =>
  get<{ insights: Insight[]; pagination: Pagination }>(`insights?page=${page}&paginate=${paginate}`)

// ─── 26 · Popular Tags ───────────────────────────────────────

export const getPopularTags = () => get<Tag[]>('popular-tags')

// ─── 27 · Contact ────────────────────────────────────────────

export const sendContact = (body: { name: string; email: string; subject?: string; message: string }) =>
  post<null>('contact', body)

// ─── 28 · Subscribe ──────────────────────────────────────────

export const subscribe = (email: string) =>
  post<null>('subscribe', { email })
